/**
 * CustomerPricesPage (/customers/:haulerOperatorId) — one customer's prices (gap 1, DEC-383,
 * D-10/D-17). One tab (hauling and disposal prices), made of named sections that render in
 * PRICE_SECTIONS order.
 *
 * The page reads the customer ONCE (GET /v1/customer-prices/:customerId, VIEW_INVOICES) and
 * hands every section the same typed CustomerContext: name, company-local today and canEdit.
 * OPEN-213 appends 'zones' and 'zone-prices'; a section must not fetch the customer again.
 * Each section fetches its own rows and shows its own error, so one failing section never
 * takes the header or the other sections down.
 *
 * canEdit is UX only: the API enforces MANAGE_CUSTOMER_PRICES on every write.
 */

import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@lodex-pro/web-shared';
import { TAB_TRACK_STYLE, tabPillStyle } from 'src/components/ui/tab-styles';
import { Button } from 'src/components/ui/button';
import { useCustomerPricesCustomer } from '../features/billing/customer-prices-hooks';
import { JobTypePricesSection } from '../features/billing/JobTypePricesSection';

/** What every price section knows about the customer; built once by the page. */
export interface CustomerContext {
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string | null;
  /** Company-local 'YYYY-MM-DD' from GET /v1/customer-prices/:customerId. */
  readonly today: string;
  /** MANAGE_CUSTOMER_PRICES, from the same response. UX only. */
  readonly canEdit: boolean;
}

export interface PriceSectionProps {
  readonly customer: CustomerContext;
}

export interface PriceSection {
  readonly id: string;
  readonly title: string;
  readonly render: React.ComponentType<PriceSectionProps>;
}

/** The tab's sections, in order. OPEN-213 appends 'zones' and 'zone-prices'. */
export const PRICE_SECTIONS: readonly PriceSection[] = [
  { id: 'job-types', title: 'Job type prices', render: JobTypePricesSection },
];

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

/** A section that throws while rendering fails alone; its query errors it shows itself. */
class SectionBoundary extends React.Component<
  { readonly children: React.ReactNode },
  { readonly failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): React.ReactNode {
    if (this.state.failed) {
      return (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          This section could not be shown. Reload the page to try again.
        </p>
      );
    }
    return this.props.children;
  }
}

export function CustomerPricesPage(): React.JSX.Element {
  const params = useParams<{ haulerOperatorId: string }>();
  const id = params.haulerOperatorId ?? '';
  const read = useCustomerPricesCustomer(id);

  const customer: CustomerContext | null = React.useMemo(
    () =>
      read.data === undefined
        ? null
        : {
            id: read.data.id,
            name: read.data.name,
            abbreviation: read.data.abbreviation,
            today: read.data.today,
            canEdit: read.data.canEdit,
          },
    [read.data],
  );
  const notFound = read.isError && read.error instanceof ApiError && read.error.status === 404;

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <Link
        to="/customers"
        style={{
          color: 'var(--color-link)',
          display: 'inline-block',
          marginBottom: '16px',
        }}
      >
        ← Customers
      </Link>
      {read.isLoading && (
        <p role="status" style={helpStyle}>
          Loading…
        </p>
      )}
      {notFound && <p style={helpStyle}>Customer not found.</p>}
      {read.isError && !notFound && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Couldn&apos;t load this customer. Reload the page to try again.
        </p>
      )}
      {customer !== null && (
        <>
          <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '16px' }}>
            {customer.name}
            {customer.abbreviation !== null && (
              <span style={{ ...helpStyle, fontSize: '14px', marginLeft: '8px' }}>
                {customer.abbreviation}
              </span>
            )}
          </h2>
          <div role="tablist" style={{ ...TAB_TRACK_STYLE, marginBottom: '20px' }}>
            <Button
              role="tab"
              id="customer-prices-tab"
              aria-selected={true}
              aria-controls="customer-prices-panel"
              variant="ghost"
              style={tabPillStyle(true)}
            >
              Hauling & disposal prices
            </Button>
          </div>
          <div role="tabpanel" id="customer-prices-panel" aria-labelledby="customer-prices-tab">
            {PRICE_SECTIONS.map((s) => {
              const Render = s.render;
              const headingId = `price-section-${s.id}`;
              return (
                <section key={s.id} aria-labelledby={headingId} style={{ marginBottom: '28px' }}>
                  <h2
                    id={headingId}
                    style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px' }}
                  >
                    {s.title}
                  </h2>
                  <SectionBoundary>
                    <Render customer={customer} />
                  </SectionBoundary>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
